import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// Theme state, deliberately matching Foundry (vdock-dock/src/lib/theme.tsx) so the
// admin console behaves the same way in both apps: dark is the default, light is
// opt-in and persisted, and the choice follows the user across tabs.
//
// The active theme is applied as document.documentElement.dataset.theme, which is
// what styles/foundry.css reads ([data-theme="light"] .foundry). The legacy
// light/dark CLASS is still written because index.css / tailwind's darkMode:"class"
// were built around it; `.dark` is a no-op there, and the PUBLIC events pages are
// light-only by design, so only the admin shell actually changes appearance.

const THEME_KEY = "vdock:theme";
const LEGACY_KEY = "vd_theme"; // pre-Foundry key; read once so nobody's choice resets

const ThemeContext = createContext(null);

/** Persisted theme, defaulting to dark. try/catch: Safari private mode throws. */
function readStoredTheme() {
  try {
    const stored = localStorage.getItem(THEME_KEY) ?? localStorage.getItem(LEGACY_KEY);
    return stored === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(readStoredTheme);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
      localStorage.setItem(LEGACY_KEY, theme);
    } catch {
      // Ignore: private mode / storage disabled. In-memory state still drives the UI.
    }

    const onStorage = (e) => {
      if (e.key !== THEME_KEY) return;
      setTheme(readStoredTheme());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [theme]);

  const toggle = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);
  const value = useMemo(() => ({ theme, toggle, setTheme }), [theme, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
